/* eslint-disable react-refresh/only-export-components -- context module */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_SITE_CONFIG,
  SITE_CONFIG_API_URL,
  SITE_PUBLIC_JSON_URL,
  type SiteConfig,
} from '../config/siteConfig';
import { FALLBACK_CAMPAIGNS, parseCampaignsPayload, type Campaign } from '../config/campaigns';
import { CAMPAIGNS_JSON_URL } from '../config/campaigns';

type SiteConfigState = {
  site: SiteConfig;
  loaded: boolean;
  source: 'api' | 'json' | 'fallback';
};

const SiteConfigContext = createContext<SiteConfigState>({
  site: DEFAULT_SITE_CONFIG,
  loaded: false,
  source: 'fallback',
});

function mergeSite(partial: Partial<SiteConfig> | null): SiteConfig {
  if (!partial) return DEFAULT_SITE_CONFIG;
  return {
    ...DEFAULT_SITE_CONFIG,
    ...partial,
    contact: { ...DEFAULT_SITE_CONFIG.contact, ...partial.contact },
    integrations: { ...DEFAULT_SITE_CONFIG.integrations, ...partial.integrations },
    seo: partial.seo ?? DEFAULT_SITE_CONFIG.seo,
    navbarLinks: {
      ...DEFAULT_SITE_CONFIG.navbarLinks,
      ...partial.navbarLinks,
    },
    campaigns: partial.campaigns ?? DEFAULT_SITE_CONFIG.campaigns,
    announcements: partial.announcements ?? DEFAULT_SITE_CONFIG.announcements,
    downloads: partial.downloads ?? DEFAULT_SITE_CONFIG.downloads,
  };
}

export function SiteConfigProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SiteConfigState>({
    site: DEFAULT_SITE_CONFIG,
    loaded: false,
    source: 'fallback',
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const apiRes = await fetch(SITE_CONFIG_API_URL, { cache: 'no-store' });
        if (apiRes.ok) {
          const data = (await apiRes.json()) as { ok?: boolean; site?: SiteConfig };
          if (!cancelled && data.site) {
            setState({ site: mergeSite(data.site), loaded: true, source: 'api' });
            return;
          }
        }
      } catch {
        /* PHP yoksa statik JSON dene */
      }

      try {
        const jsonRes = await fetch(SITE_PUBLIC_JSON_URL, { cache: 'no-store' });
        if (jsonRes.ok) {
          const site = (await jsonRes.json()) as SiteConfig;
          if (!cancelled) {
            setState({ site: mergeSite(site), loaded: true, source: 'json' });
            return;
          }
        }
      } catch {
        /* dev fallback */
      }

      try {
        const legacyRes = await fetch(CAMPAIGNS_JSON_URL, { cache: 'no-store' });
        if (legacyRes.ok) {
          const data = await legacyRes.json();
          const campaigns = parseCampaignsPayload(data);
          if (!cancelled && campaigns.length > 0) {
            setState({
              site: mergeSite({ campaigns }),
              loaded: true,
              source: 'json',
            });
            return;
          }
        }
      } catch {
        /* ignore */
      }

      if (!cancelled) {
        setState({ site: DEFAULT_SITE_CONFIG, loaded: true, source: 'fallback' });
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return <SiteConfigContext.Provider value={state}>{children}</SiteConfigContext.Provider>;
}

export function useSiteConfig(): SiteConfigState {
  return useContext(SiteConfigContext);
}

export function useSiteCampaigns(): { campaigns: Campaign[]; loaded: boolean } {
  const { site, loaded } = useSiteConfig();
  return useMemo(
    () => ({
      campaigns: site.campaigns.length > 0 ? site.campaigns : FALLBACK_CAMPAIGNS,
      loaded,
    }),
    [site.campaigns, loaded],
  );
}
